import { useState } from "react";
import { useNavigate } from "react-router";
import { Lock } from "lucide-react";
import { passwordChangeStateFor } from "@/modules/identity/commands/authCopy";
import { Banner, Field } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import { AuthHeading, SubmitButton } from "~/components/forms";
import { useMe } from "~/auth/MeProvider";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * StaffPasswordChangeScreen (docs/ux/screens/StaffPasswordChangeScreen.md), H44.
 *
 * **There is no "skip" and no "later".** The password this replaces is sitting
 * in a WhatsApp thread and will stay there forever, so the window in which it is
 * the only thing protecting the account should be minutes. The only way off
 * this screen is to change the password or to log out.
 *
 * The gate is enforced on the server on every command regardless; what this
 * screen must get right is not *stopping* anybody, it is not accidentally
 * closing itself. Its route guard is deliberately the one that allows a caller
 * whose `passwordChangeRequired` is still set.
 */
export function StaffPasswordChangeScreen() {
  const navigate = useNavigate();
  const { reload, signOut } = useMe();
  const { state, pending, submit } = useForm();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const changed = await submit(
      () =>
        post("/auth/staff/password", {
          currentPassword,
          newPassword,
          confirmPassword,
        }),
      passwordChangeStateFor,
    );
    if (!changed) return;

    // `/me` carries the flag this screen exists to clear, so it is re-read
    // before the navigation: leaving with a stale answer sends them to
    // /business, whose guard reads the old flag and returns them here.
    await reload();
    void navigate("/business", { replace: true });
  }

  return (
    <AuthShell>
      <AuthHeading
        title="Choose your password"
        sub="This password was sent to you in a message. Choose one only you know."
      />

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Current password"
            name="currentPassword"
            type="password"
            value={currentPassword}
            onChange={setCurrentPassword}
            error={state.fieldErrors?.currentPassword}
            autoComplete="current-password"
            help="The one we sent you."
            icon={Lock}
          />
          <Field
            label="New password"
            name="newPassword"
            type="password"
            value={newPassword}
            onChange={setNewPassword}
            error={state.fieldErrors?.newPassword}
            autoComplete="new-password"
            icon={Lock}
          />
          <Field
            label="Confirm new password"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={state.fieldErrors?.confirmPassword}
            autoComplete="new-password"
            icon={Lock}
          />
          <SubmitButton pending={pending} pendingLabel="Saving…">
            Save password
          </SubmitButton>
        </fieldset>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          className="text-sm font-medium text-muted hover:text-ink"
          onClick={() => {
            void signOut().then(() => navigate("/", { replace: true }));
          }}
        >
          Log out
        </button>
      </div>
    </AuthShell>
  );
}
